const MENTION_AGENTS_ONBOARDING_FEATURE_ID = 'mention-agents-onboarding';
const SOURCE_CONTROL_UNAVAILABLE_FEATURE_ID = 'source-control-unavailable';

export type MentionAgentsSpotlightFeatureId =
	| typeof MENTION_AGENTS_ONBOARDING_FEATURE_ID
	| typeof SOURCE_CONTROL_UNAVAILABLE_FEATURE_ID;

export type AcknowledgedSpotlights = Readonly<Record<MentionAgentsSpotlightFeatureId, boolean>>;

const NO_ACKNOWLEDGED_SPOTLIGHTS: AcknowledgedSpotlights = {
	[MENTION_AGENTS_ONBOARDING_FEATURE_ID]: false,
	[SOURCE_CONTROL_UNAVAILABLE_FEATURE_ID]: false,
};

type AcknowledgedSpotlightsPageStore = {
	snapshot: AcknowledgedSpotlights;
	listeners: Set<() => void>;
};

const ACKNOWLEDGED_SPOTLIGHTS_PAGE_STORE_KEY = Symbol.for(
	'rovo-chat.mention-agents.acknowledged-spotlights',
);

// The sidebar can recreate this module when it closes and reopens. Store the two flags and their
// listeners on the page global so every copy of the sidebar bundle shares the same page-lifetime
// acknowledgement state. A full page load resets the flags.
const getPageStore = (): AcknowledgedSpotlightsPageStore => {
	const existingStore = Reflect.get(globalThis, ACKNOWLEDGED_SPOTLIGHTS_PAGE_STORE_KEY) as
		| AcknowledgedSpotlightsPageStore
		| undefined;
	if (existingStore) {
		return existingStore;
	}

	const pageStore: AcknowledgedSpotlightsPageStore = {
		snapshot: NO_ACKNOWLEDGED_SPOTLIGHTS,
		listeners: new Set(),
	};
	Reflect.set(globalThis, ACKNOWLEDGED_SPOTLIGHTS_PAGE_STORE_KEY, pageStore);
	return pageStore;
};

const subscribeToAcknowledgements = (listener: () => void): (() => void) => {
	const pageStore = getPageStore();
	pageStore.listeners.add(listener);

	return () => {
		pageStore.listeners.delete(listener);
	};
};

const getAcknowledgedSpotlights = (): AcknowledgedSpotlights => getPageStore().snapshot;

const acknowledgeSpotlight = (featureId: MentionAgentsSpotlightFeatureId): void => {
	const pageStore = getPageStore();
	if (pageStore.snapshot[featureId]) {
		return;
	}

	pageStore.snapshot = {
		...pageStore.snapshot,
		[featureId]: true,
	};
	pageStore.listeners.forEach((listener) => listener());
};

type MentionAgentsSpotlightAcknowledgementStore = {
	mentionAgentsOnboardingFeatureId: typeof MENTION_AGENTS_ONBOARDING_FEATURE_ID;
	sourceControlUnavailableFeatureId: typeof SOURCE_CONTROL_UNAVAILABLE_FEATURE_ID;
	subscribe: typeof subscribeToAcknowledgements;
	getSnapshot: typeof getAcknowledgedSpotlights;
	getServerSnapshot: () => AcknowledgedSpotlights;
	acknowledge: typeof acknowledgeSpotlight;
};

export const mentionAgentsSpotlightAcknowledgementStore: MentionAgentsSpotlightAcknowledgementStore =
	{
		mentionAgentsOnboardingFeatureId: MENTION_AGENTS_ONBOARDING_FEATURE_ID,
		sourceControlUnavailableFeatureId: SOURCE_CONTROL_UNAVAILABLE_FEATURE_ID,
		subscribe: subscribeToAcknowledgements,
		getSnapshot: getAcknowledgedSpotlights,
		getServerSnapshot: () => NO_ACKNOWLEDGED_SPOTLIGHTS,
		acknowledge: acknowledgeSpotlight,
	};
